import { ResultItem, ResultsSectionProps } from '@common/types/search';
import { HighlightText } from '@components/display/richTexts';
import { Button } from '@components/utility/buttons';
import { APP_PREFIX_PATH } from '@constants/common';
import { EXTERNAL_URL_REGEX } from '@constants/search';
import { useLazyDownload } from '@hooks/useDownload';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { handleError } from '@utils/common';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

const NEXT_RESULTS_STEP = 20;

export const SearchResultsSection = <T extends ResultItem>({
    Icon,
    name,
    search,
    results,
    totalResultsCount,
    onShowNext,
    loading,
    urlIn,
    showNext = true,
    downloadable = false,
    chip,
    title,
}: ResultsSectionProps<T>) => {
    const displayedCount = results.length;
    const totalCount = totalResultsCount ?? displayedCount;
    const remainingCount = Math.max(totalCount - displayedCount, 0);
    const nextCount = Math.min(remainingCount, NEXT_RESULTS_STEP);
    const intl = useIntl();

    const [lazyDownload] = useLazyDownload();
    const handleDownload = async (url: string) => {
        const isExternal = EXTERNAL_URL_REGEX.test(url);
        try {
            await lazyDownload(isExternal ? { absolutePath: url } : { relativePath: url });
        } catch (error) {
            handleError(
                intl.formatMessage({ id: 'search.results.downloadError', defaultMessage: 'Error downloading file' }),
                error
            );
        }
    };

    return (
        <section>
            <Stack direction="row" alignItems="center" gap="6px" mb="8px">
                <Icon width={16} height={16} sx={{ fontSize: 16 }} />
                <Typography fontSize={14} letterSpacing={0} lineHeight="21px">
                    {loading ? (
                        <Skeleton variant="text" width={100} />
                    ) : totalCount > 0 ? (
                        `${totalCount} ${name}`
                    ) : (
                        intl.formatMessage(
                            { id: 'search.results.notFound', defaultMessage: 'No {name} found' },
                            { name: name.toLowerCase() }
                        )
                    )}
                </Typography>
            </Stack>
            <Divider />
            {loading ? (
                <Stack direction="column" my="16px">
                    {results.map((_, index) => (
                        <Skeleton key={`skeleton-${index}`} variant="text" />
                    ))}
                </Stack>
            ) : (
                totalCount > 0 && (
                    <Stack direction="column" my="16px">
                        {results.map((result, index) => {
                            const resultUrl =
                                typeof result.url === 'string' ? { href: result.url, target: undefined } : result.url;
                            const opensInNewTab = Boolean(resultUrl.target);
                            const isAbsoluteUrl = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(resultUrl.href);
                            const href =
                                urlIn &&
                                !resultUrl.target &&
                                !isAbsoluteUrl &&
                                resultUrl.href !== APP_PREFIX_PATH &&
                                !resultUrl.href.startsWith(`${APP_PREFIX_PATH}/`)
                                    ? `${APP_PREFIX_PATH}/${resultUrl.href.replace(/^\/+/, '')}`
                                    : resultUrl.href;
                            const resultChip = chip?.(result);

                            return (
                                <Typography
                                    key={`${name}-result-${index}`}
                                    fontSize={16}
                                    letterSpacing={0}
                                    lineHeight="24px"
                                    p="4px"
                                    display="flex"
                                    alignItems="center"
                                    gap="8px"
                                    className={clsx('line-clamp-2', {
                                        'relative group transition-colors': downloadable,
                                    })}
                                    {...(!(downloadable && !opensInNewTab)
                                        ? {
                                              component: urlIn ? Link : 'a',
                                              to: urlIn ? href : undefined,
                                              href: urlIn ? undefined : resultUrl.href,
                                              target: resultUrl.target,
                                              rel: resultUrl.target ? 'noopener noreferrer' : undefined,
                                          }
                                        : {
                                              sx: {
                                                  cursor: 'pointer',
                                                  borderRadius: '8px',
                                                  '&:hover': { bgcolor: 'secondary-alt.light' },
                                              },
                                              onClick: () => handleDownload(resultUrl.href),
                                          })}
                                >
                                    {result.type || resultChip ? (
                                        <Chip
                                            variant="outlined"
                                            size="small"
                                            label={resultChip ?? result.type?.toUpperCase()}
                                            className="mr-1"
                                        />
                                    ) : null}
                                    <span>
                                        <HighlightText text={title ? title(result) : result.title} term={search} />
                                    </span>
                                    {downloadable && (
                                        <FileDownloadOutlinedIcon className="absolute right-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    )}
                                </Typography>
                            );
                        })}
                    </Stack>
                )
            )}
            {loading ? (
                <Skeleton variant="rounded" className="rounded-full" width={200} height={26} />
            ) : (
                remainingCount > 0 &&
                showNext && (
                    <Button.Neutral onClick={() => onShowNext?.(displayedCount + nextCount)}>
                        {intl.formatMessage(
                            { id: 'search.results.showNext', defaultMessage: 'Show next {count} {name} results' },
                            { count: nextCount, name: name.toLowerCase() }
                        )}
                    </Button.Neutral>
                )
            )}
        </section>
    );
};
